import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Camera,
  ChevronRight,
  Euro,
  Home,
  Loader2,
  User,
  XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const formatDateFr = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getPhotoUrl = (p) => {
  if (!p) return '';
  if (p.startsWith('http')) return p;
  const token = localStorage.getItem('token');
  return `${API}/api/photo?path=${encodeURIComponent(p)}&auth=${token}`;
};

const monthOptions = () => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value: ym, label: d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) });
  }
  return out;
};

/**
 * Vue partagée Admin + Gouvernant : liste des prestations refusées avec photos.
 * Reliée à GET /api/admin/rejections.
 */
const RejectionsListPage = ({ role = 'admin' }) => {
  const navigate = useNavigate();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState({ month: defaultMonth, total: 0, total_lost: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/admin/rejections`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des rejets");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const backPath = role === 'gouvernant' ? '/gouvernant' : '/admin';
  const taskPath = (id) => `/admin/tasks/${id}`;
  const noData = !loading && (data.items || []).length === 0;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h1 className="text-2xl md:text-3xl font-black text-[#0F172A]">Prestations refusées</h1>
            </div>
            <p className="text-slate-500 text-sm">Mauvais ménage — motifs et photos.</p>
          </div>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[180px]" data-testid="month-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions().map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="border-red-200">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-red-100 p-3 rounded-xl">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Rejets</p>
                <p className="text-3xl font-black text-[#0F172A]" data-testid="kpi-count">{data.total}</p>
                <p className="text-xs text-slate-500">en {monthLabel(data.month)}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-xl">
                <Euro className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Montant annulé</p>
                <p className="text-3xl font-black text-[#0F172A]">{data.total_lost.toFixed(2)} €</p>
                <p className="text-xs text-slate-500">paiements supprimés</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : noData ? (
          <Card>
            <CardContent className="py-16 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto text-slate-200 mb-3" />
              <p className="text-slate-600 font-semibold">Aucune prestation refusée</p>
              <p className="text-sm text-slate-400 mt-1">pour {monthLabel(data.month)}.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-3">
            {data.items.map((it) => (
              <Card
                key={it.task_id}
                className="cursor-pointer hover:border-red-300 transition-colors"
                onClick={() => navigate(taskPath(it.task_id))}
                data-testid={`reject-item-${it.task_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Home className="h-4 w-4 text-slate-400 shrink-0" />
                        <p className="font-bold text-[#0F172A] truncate">{it.property_name}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-2">
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDateFr(it.date)}</span>
                        <span className="flex items-center gap-1"><User className="h-3 w-3" />{(it.housekeeper_names || []).join(', ') || '—'}</span>
                        {typeof it.price === 'number' && (
                          <span className="flex items-center gap-1 font-semibold text-red-600">
                            <Euro className="h-3 w-3" />{it.price.toFixed(2)} €
                          </span>
                        )}
                      </div>
                      {it.reason && (
                        <div className="bg-red-50 border border-red-100 rounded-lg p-2.5 mb-2">
                          <p className="text-xs font-bold text-red-700 uppercase mb-0.5">Motif</p>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{it.reason}</p>
                        </div>
                      )}
                      {it.photos && it.photos.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {it.photos.slice(0, 5).map((p, i) => (
                              <img
                                key={i}
                                src={getPhotoUrl(p)}
                                alt=""
                                className="h-14 w-14 rounded object-cover border border-red-200 cursor-zoom-in"
                                onClick={(e) => { e.stopPropagation(); window.open(getPhotoUrl(p), '_blank'); }}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <Camera className="h-3 w-3" />
                            {it.photos.length} photo{it.photos.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Refusé par <strong>{it.rejected_by}</strong>
                        {it.rejected_at && <> · {new Date(it.rejected_at).toLocaleDateString('fr-FR')}</>}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-slate-300 shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RejectionsListPage;
